<template>
  <div class="gs-rte" :class="{ 'is-disabled': disabled, 'is-html': htmlView }">
    <div v-if="variables.length" class="gs-rte__variables" role="group" aria-label="Rich-text variables">
      <span>Personalize this content</span>
        <div v-if="variables.length" class="gs-rte__insert" v-click-outside="closeInsert">
          <button type="button" data-action="insert-variable" class="gs-rte__btn gs-rte__btn--label" :aria-expanded="insertOpen ? 'true' : 'false'" :disabled="disabled" @mousedown.prevent @click="toggleInsert()"><gs-icon name="plus" size="14"/> Insert variable <gs-icon name="chevron-down" size="14"/></button>
          <div v-if="insertOpen" ref="insertMenu" class="gs-rte__menu" :class="{ 'is-up': insertUp }" role="menu">
            <button v-for="v in variables" :key="v.token" type="button" role="menuitem" class="gs-rte__menu-item" @click="insertVariable(v); insertOpen = false">{{ v.label }}</button>
          </div>
        </div>
    </div>
    <div class="gs-rte__toolbar" role="toolbar" aria-label="Formatting">
      <div class="gs-rte__group">
        <button type="button" :disabled="toolsDisabled" data-action="bold" :class="btnClass('bold')" title="Bold" aria-label="Bold" @click="run('bold')"><gs-icon name="bold" size="16"/></button>
        <button type="button" :disabled="toolsDisabled" data-action="italic" :class="btnClass('italic')" title="Italic" aria-label="Italic" @click="run('italic')"><gs-icon name="italic" size="16"/></button>
        <button type="button" :disabled="toolsDisabled" data-action="underline" :class="btnClass('underline')" title="Underline" aria-label="Underline" @click="run('underline')"><gs-icon name="underline" size="16"/></button>
        <button type="button" :disabled="toolsDisabled" data-action="strike" :class="btnClass('strike')" title="Strikethrough" aria-label="Strikethrough" @click="run('strike')"><gs-icon name="strikethrough" size="16"/></button>
      </div>
      <div class="gs-rte__group">
        <select class="gs-rte__select" data-action="text-style" :disabled="toolsDisabled" :value="headingLevel" @change="setHeading($event.target.value)" aria-label="Text style">
          <option value="0">Paragraph</option><option value="1">Heading 1</option><option value="2">Heading 2</option><option value="3">Heading 3</option>
          <option v-if="legacyHeadingLevel" :value="legacyHeadingLevel">Heading {{ legacyHeadingLevel }}</option>
        </select>
      </div>
      <div class="gs-rte__group">
        <span class="gs-rte__colour" :class="{ 'is-set': !!textColour }">
          <input
            type="color"
            class="gs-rte__colour-input"
            data-action="text-colour"
            :disabled="toolsDisabled"
            :value="textColour || '#182230'"
            title="Text colour"
            aria-label="Text colour"
            @change="setStyleProperty('color', $event.target.value)"
          >
          <span class="gs-rte__colour-bar" :style="{ background: textColour || 'transparent' }" aria-hidden="true"></span>
        </span>
        <button type="button" :disabled="toolsDisabled" data-action="clear-text-colour" class="gs-rte__btn gs-rte__btn--tiny" title="Clear text colour" aria-label="Clear text colour" @click="setStyleProperty('color', null)"><gs-icon name="x" size="12"/></button>

        <span class="gs-rte__colour" :class="{ 'is-set': !!backgroundColour }">
          <input
            type="color"
            class="gs-rte__colour-input"
            data-action="background-colour"
            :disabled="toolsDisabled"
            :value="backgroundColour || '#ffffff'"
            title="Background colour"
            aria-label="Background colour"
            @change="setStyleProperty('background-color', $event.target.value)"
          >
          <span class="gs-rte__colour-bar gs-rte__colour-bar--fill" :style="{ background: backgroundColour || 'transparent' }" aria-hidden="true"></span>
        </span>
        <button type="button" :disabled="toolsDisabled" data-action="clear-background-colour" class="gs-rte__btn gs-rte__btn--tiny" title="Clear background colour" aria-label="Clear background colour" @click="setStyleProperty('background-color', null)"><gs-icon name="x" size="12"/></button>

        <select class="gs-rte__select" data-action="font-size" :disabled="toolsDisabled" :value="fontSizeValue" aria-label="Text size" title="Text size" @change="setStyleProperty('font-size', $event.target.value)">
          <option v-for="size in fontSizes" :key="size.label" :value="size.value">{{ size.label }}</option>
          <option v-if="customFontSize" :value="customFontSize" disabled>Custom ({{ customFontSize }})</option>
        </select>

        <select class="gs-rte__select" data-action="font-family" :disabled="toolsDisabled" :value="fontFamilyValue" aria-label="Font" title="Font" @change="setStyleProperty('font-family', $event.target.value)">
          <option v-for="font in fontFamilies" :key="font.label" :value="font.value">{{ font.label }}</option>
        </select>
      </div>
      <div class="gs-rte__group">
        <button type="button" :disabled="toolsDisabled" data-action="bullet_list" :class="btnClass('bullet_list')" title="Bulleted list" aria-label="Bulleted list" @click="run('bullet_list')"><gs-icon name="list-ul" size="16"/></button>
        <button type="button" :disabled="toolsDisabled" data-action="ordered_list" :class="btnClass('ordered_list')" title="Numbered list" aria-label="Numbered list" @click="run('ordered_list')"><gs-icon name="list-ol" size="16"/></button>
      </div>
      <div class="gs-rte__group">
        <button v-for="a in alignments" :key="a" type="button" :disabled="toolsDisabled" :data-action="`align-${a}`" :class="btnClass('alignment', { textAlign: a })" :title="`Align ${a}`" :aria-label="`Align ${a}`" @click="run('alignment', { textAlign: a })"><gs-icon :name="`align-${a}`" size="16"/></button>
      </div>
      <div class="gs-rte__group">
        <button type="button" :disabled="toolsDisabled" data-action="link" :class="btnClass('link')" title="Link" aria-label="Link" @click="openLinkPopover()"><gs-icon name="link" size="16"/></button>
        <button type="button" :disabled="toolsDisabled" data-action="image" class="gs-rte__btn" title="Insert image" aria-label="Insert image" @click="pickImage()"><gs-icon name="image" size="16"/></button>
        <input ref="file" type="file" accept="image/*" class="gs-rte__file" @change="onFileChosen($event)">
        <button type="button" :disabled="toolsDisabled" data-action="hr" class="gs-rte__btn" title="Divider" aria-label="Divider" @click="run('horizontal_rule')"><gs-icon name="minus" size="16"/></button>
        <button type="button" :disabled="toolsDisabled" data-action="clear" class="gs-rte__btn" title="Clear formatting" aria-label="Clear formatting" @click="clearFormatting()"><gs-icon name="eraser" size="16"/></button>
      </div>
      <div class="gs-rte__group">
        <button type="button" :disabled="toolsDisabled" data-action="undo" class="gs-rte__btn" title="Undo" aria-label="Undo" @click="run('undo')"><gs-icon name="undo" size="16"/></button>
        <button type="button" :disabled="toolsDisabled" data-action="redo" class="gs-rte__btn" title="Redo" aria-label="Redo" @click="run('redo')"><gs-icon name="redo" size="16"/></button>
      </div>
      <div class="gs-rte__group gs-rte__group--end">
        <div class="gs-rte__view" role="group" aria-label="Editor view">
          <button type="button" data-action="view-visual" :class="{ 'is-active': !htmlView }" @click="$emit('update:htmlView', false)">Visual</button>
          <button type="button" data-action="view-html" :class="{ 'is-active': htmlView }" @click="$emit('update:htmlView', true)">{{ $t('frontend_resources_view_html') }}</button>
        </div>
        <slot name="toolbar-end"/>
      </div>
    </div>

    <div v-if="linkOpen" class="gs-rte__popover">
      <gs-field label="Link URL"><input ref="linkInput" v-model="linkHref" class="gs-rte__input" placeholder="https://" @keydown.enter.prevent="applyLink()"></gs-field>
      <div class="gs-rte__popover-actions">
        <gs-button variant="ghost" size="sm" @click="removeLink()">Remove</gs-button>
        <gs-button variant="outline" size="sm" @click="linkOpen = false">Cancel</gs-button>
        <gs-button variant="primary" size="sm" @click="applyLink()">Apply</gs-button>
      </div>
    </div>

    <textarea v-if="htmlView" ref="html" class="gs-rte__html" :value="value" :placeholder="placeholder" :disabled="disabled" :style="{ minHeight }" @input="$emit('input', $event.target.value)"></textarea>
    <editor-content v-else class="gs-rte__content" :style="{ minHeight }" :editor="editor"/>

    <div v-if="selectedImage" class="gs-rte__image-bubble" role="toolbar" aria-label="Image options">
      <button v-for="w in imageWidths" :key="w" type="button" :disabled="toolsDisabled" :aria-label="`Width ${w}`" :class="{ 'is-active': selectedImageAttrs.width === w }" @click="setImageAttr({ width: w })">{{ w }}</button>
      <button v-for="a in imageAligns" :key="a" type="button" :disabled="toolsDisabled" :aria-label="`Align ${a}`" :class="{ 'is-active': selectedImageAttrs.align === a }" :title="`Align ${a}`" @click="setImageAttr({ align: a })"><gs-icon :name="`align-${a}`" size="14"/></button>
      <button type="button" :disabled="toolsDisabled" class="is-destructive" title="Remove image" aria-label="Remove image" @click="deleteImage()"><gs-icon name="trash" size="14"/></button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Editor, EditorContent } from 'tiptap';
import {
  Bold,
  Italic,
  Underline,
  Strike,
  Code,
  Blockquote,
  CodeBlock,
  BulletList,
  OrderedList,
  Link,
  HorizontalRule,
  History,
  Placeholder,
  HardBreak,
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from 'tiptap-extensions';
import ResizableImage from './ResizableImage';
import StyledSpan, { activeStyledSpan, parseStyleAttribute } from './StyledSpan';
import StyledListItem from './StyledListItem';
import { Alignment, AlignedParagraph, AlignedHeading } from './alignment';
import { normaliseQuillHtml, unwrapListItemParagraphs } from './quillHtmlNormaliser';
import { insertionPlan, insertTokenAtSelection } from './editorVariables';
import { validateImageFile, uploadImage } from './imageUpload';
import './gs-rich-text-editor.css';

const MARKS_TO_CLEAR = ['bold', 'italic', 'underline', 'strike', 'link'];

const UPLOAD_MESSAGES = {
  'not-image': 'Only image files can be added.',
  'too-large': 'That image is too large.',
  'no-upload-url': 'Image uploads are not available here.',
  'upload-failed': 'The image could not be uploaded. Please try again.',
};

const RGB = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i;
const HEX_SHORT = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;

/**
 * `<input type="color">` only understands `#rrggbb`, so the stored value -
 * which may be `rgb(...)` from a legacy Quill asset, or a colour keyword - is
 * converted where possible and reported as '' (meaning "not set") otherwise.
 */
function toHexColour(value) {
  const colour = (value || '').trim();
  if (!colour) return '';
  if (/^#[0-9a-f]{6}$/i.test(colour)) return colour.toLowerCase();

  const short = colour.match(HEX_SHORT);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase();

  const rgb = colour.match(RGB);
  if (rgb) {
    return `#${[rgb[1], rgb[2], rgb[3]]
      .map(part => Math.min(255, Number(part)).toString(16).padStart(2, '0'))
      .join('')}`;
  }

  return '';
}

/**
 * Legacy assets carry full stacks such as `Georgia, "Times New Roman", serif`;
 * they are folded onto the three options the select offers so the control
 * tells the truth about the text under the cursor.
 */
function normaliseFontFamily(value) {
  const family = (value || '').toLowerCase();
  if (!family) return '';
  if (family.indexOf('monospace') !== -1 || family.indexOf('courier') !== -1 || family.indexOf('monaco') !== -1) return 'monospace';
  if (family.indexOf('sans-serif') !== -1) return '';
  if (family.indexOf('serif') !== -1 || family.indexOf('georgia') !== -1 || family.indexOf('times') !== -1) return 'serif';
  return '';
}

function firstImageFile(fileList) {
  if (!fileList || !fileList.length) return null;
  return Array.prototype.slice.call(fileList).filter(f => /^image\//.test(f.type))[0] || null;
}

export default {
  name: 'GsRichTextEditor',

  components: { EditorContent },

  // `v-click-outside` is not registered globally in groovesell-ui, so the
  // directive is declared locally instead of relying on a plugin.
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.__gsClickOutside__ = (event) => {
          if (el === event.target || el.contains(event.target)) return;
          if (typeof binding.value === 'function') binding.value(event);
        };
        document.addEventListener('click', el.__gsClickOutside__, true);
      },
      unbind(el) {
        if (!el.__gsClickOutside__) return;
        document.removeEventListener('click', el.__gsClickOutside__, true);
        delete el.__gsClickOutside__;
      },
    },
  },

  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    variables: { type: Array, default: () => [] },
    uploadUrl: { type: String, default: '' },
    maxImageBytes: { type: Number, default: 2097152 },
    disabled: { type: Boolean, default: false },
    htmlView: { type: Boolean, default: false },
    minHeight: { type: String, default: '320px' },
  },

  data() {
    return {
      editor: null,
      insertOpen: false,
      // Set on open when the menu would run past the bottom of the viewport.
      insertUp: false,
      linkOpen: false,
      linkHref: '',
      selectedImage: null,
      lastEmitted: '',
      // Bumped on every ProseMirror transaction so the toolbar re-renders and
      // re-evaluates its active states.
      tick: 0,
      alignments: ['left', 'center', 'right'],
      // Values match what Quill's own `size` / `font` formats rendered, so a
      // legacy asset lands on the right option instead of looking unset.
      fontSizes: [
        { label: 'Small', value: '0.75em' },
        { label: 'Normal', value: '' },
        { label: 'Large', value: '1.5em' },
        { label: 'Huge', value: '2.5em' },
      ],
      fontFamilies: [
        { label: 'Sans Serif', value: '' },
        { label: 'Serif', value: 'serif' },
        { label: 'Monospace', value: 'monospace' },
      ],
      imageWidths: ['25%', '50%', '75%', '100%'],
      imageAligns: ['left', 'center', 'right'],
    };
  },

  computed: {
    headingLevel() {
      const dependency = this.tick; // eslint-disable-line no-unused-vars
      const block = this.activeBlock();
      if (block && block.type && block.type.name === 'heading') {
        return String(block.attrs.level || 1);
      }
      return '0';
    },

    // The `style` declarations of the StyledSpan mark under the cursor, which
    // is what the colour swatches and the size/font selects display.
    cursorStyle() {
      const dependency = this.tick; // eslint-disable-line no-unused-vars
      if (!this.editor || !this.editor.state || !this.editor.schema) return {};
      const type = this.editor.schema.marks.styled_span;
      if (!type) return {};
      const mark = activeStyledSpan(this.editor.state, type);
      return mark ? parseStyleAttribute(mark.attrs.style) : {};
    },

    textColour() {
      return toHexColour(this.cursorStyle.color);
    },

    backgroundColour() {
      return toHexColour(this.cursorStyle['background-color']);
    },

    // A legacy size none of the presets cover - `font-size: 18px` from a Quill
    // asset, say. Same treatment as `legacyHeadingLevel`: the select gains a
    // read-only option for as long as it is needed rather than claiming the
    // text is at the default size.
    customFontSize() {
      const size = this.cursorStyle['font-size'] || '';
      return size && !this.fontSizes.some(option => option.value === size) ? size : '';
    },

    fontSizeValue() {
      const size = this.cursorStyle['font-size'] || '';
      return this.fontSizes.some(option => option.value === size) ? size : this.customFontSize;
    },

    fontFamilyValue() {
      return normaliseFontFamily(this.cursorStyle['font-family']);
    },

    // Headings 4-6 have no toolbar entry of their own. When the cursor sits in
    // one (only possible in legacy Quill content) the select gains a matching
    // option for as long as it is needed, so the control never lies about the
    // block it is describing.
    legacyHeadingLevel() {
      const level = Number(this.headingLevel);
      return level > 3 ? String(level) : '';
    },

    toolsDisabled() {
      // The rich-text commands operate on the ProseMirror document, which is
      // deliberately not kept in sync while the raw HTML textarea is being
      // edited - running them there would overwrite what was typed.
      return this.disabled || this.htmlView;
    },

    selectedImageAttrs() {
      return this.selectedImage && this.selectedImage.attrs ? this.selectedImage.attrs : {};
    },
  },

  watch: {
    value(html) {
      if (this.htmlView || !this.editor) return;
      if (html === this.lastEmitted) return;
      this.editor.setContent(normaliseQuillHtml(html), false);
      // The editor now shows `html`, so that is what the next incoming value
      // has to be compared against. Without this, a parent that swaps the
      // value away and back (A -> B -> A) would early-return on the way back
      // and leave B on screen while reporting A.
      this.lastEmitted = html;
    },

    htmlView(next, previous) {
      this.closeInsert();
      this.linkOpen = false;
      // The bubble drives ProseMirror directly, so it must not survive into
      // the HTML view.
      if (next) this.selectedImage = null;
      if (previous && !next) {
        // Typed HTML has to be pushed back into ProseMirror when the visual
        // view comes back.
        this.lastEmitted = this.value;
        this.$nextTick(() => {
          if (this.editor) this.editor.setContent(normaliseQuillHtml(this.value), false);
        });
      }
    },

    disabled(value) {
      if (this.editor) this.editor.setOptions({ editable: !value });
    },
  },

  mounted() {
    this.editor = new Editor({
      editable: !this.disabled,
      content: normaliseQuillHtml(this.value),
      extensions: [
        new AlignedParagraph(),
        new Bold(),
        new Italic(),
        new Underline(),
        new Strike(),
        // Levels 4-6 are not offered by the toolbar; they are here so headings
        // in swipes written with the old Quill editor survive a round trip.
        new AlignedHeading({ levels: [1, 2, 3, 4, 5, 6] }),
        new Code(),
        new Blockquote(),
        new CodeBlock(),
        new BulletList(),
        new OrderedList(),
        new StyledListItem(),
        new Link({ openOnClick: false }),
        new HorizontalRule(),
        new History(),
        new HardBreak(),
        new ResizableImage(),
        // Preservation only - see StyledSpan and the Table group below. None of
        // these have a toolbar control; they exist so legacy Quill content is
        // not silently deleted when an old asset is opened and saved.
        new StyledSpan(),
        new Table(),
        new TableHeader(),
        new TableCell(),
        new TableRow(),
        new Alignment(),
        new Placeholder({ emptyNodeText: this.placeholder }),
      ],
      onUpdate: ({ getHTML }) => {
        // The document is emitted in the shape the promo area stores, not the
        // shape the schema keeps it in - see `unwrapListItemParagraphs`.
        const html = unwrapListItemParagraphs(getHTML());
        this.lastEmitted = html;
        this.$emit('input', html);
      },
      // TipTap v1 has no `onSelectionUpdate`; `onTransaction` fires for both
      // document and selection changes.
      onTransaction: () => {
        this.tick += 1;
        this.trackSelectedImage();
      },
    });

    this.$el.addEventListener('drop', this.onDrop, true);
    this.$el.addEventListener('paste', this.onPaste, true);

    this.$emit('ready', this.editor);
  },

  beforeDestroy() {
    this.$el.removeEventListener('drop', this.onDrop, true);
    this.$el.removeEventListener('paste', this.onPaste, true);
    if (this.editor) this.editor.destroy();
  },

  methods: {
    run(name, attrs) {
      if (this.toolsDisabled) return;
      if (!this.editor || typeof this.editor.commands[name] !== 'function') return;
      this.editor.commands[name](attrs);
    },

    btnClass(name, attrs) {
      return ['gs-rte__btn', { 'is-active': this.isActiveControl(name, attrs) }];
    },

    isActiveControl(name, attrs) {
      const dependency = this.tick; // eslint-disable-line no-unused-vars
      if (!this.editor) return false;

      if (name === 'alignment') {
        const block = this.activeBlock();
        if (!block || !block.attrs) return false;
        const current = block.attrs.textAlign || 'left';
        return current === ((attrs && attrs.textAlign) || 'left');
      }

      const check = this.editor.isActive && this.editor.isActive[name];
      return typeof check === 'function' ? Boolean(check(attrs)) : false;
    },

    activeBlock() {
      if (!this.editor || !this.editor.state || !this.editor.state.selection) return null;
      const { $from } = this.editor.state.selection;
      return $from && $from.parent ? $from.parent : null;
    },

    setHeading(value) {
      const level = Number(value) || 0;
      if (!level) this.run('paragraph');
      else this.run('heading', { level });
    },

    /**
     * Merge one style property into the StyledSpan mark over the selection.
     * A null/empty value removes that property (and the span with it, once
     * nothing is left).
     */
    setStyleProperty(property, value) {
      if (this.toolsDisabled || !this.editor) return;
      // Focus *before* the command: tiptap's focus() dispatches a setSelection
      // transaction when the view is blurred, and that nulls storedMarks. Doing
      // it afterwards threw away the style a collapsed selection had just been
      // given, so picking a colour and then typing did nothing.
      this.editor.focus();
      this.run('styled_span', { property, value });
    },

    // The menu is opened from here so its height can be measured before the
    // user sees it: a funnel with many landing pages produced a list that ran
    // off the bottom of a short viewport with no way to reach the last items.
    toggleInsert() {
      if (this.insertOpen) {
        this.closeInsert();
        return;
      }

      this.insertOpen = true;
      this.insertUp = false;

      this.$nextTick(() => {
        const menu = this.$refs.insertMenu;
        if (!menu || typeof menu.getBoundingClientRect !== 'function') return;

        const rect = menu.getBoundingClientRect();
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        const spaceBelow = viewport - rect.top;

        this.insertUp = rect.bottom > viewport && rect.top > spaceBelow;
      });
    },

    closeInsert() {
      this.insertOpen = false;
      this.insertUp = false;
    },

    /**
     * Public: insert a variable token following `insertionPlan`.
     */
    insertVariable(variable) {
      const { token, kind } = variable || {};
      if (!token || this.disabled) return;

      if (this.htmlView) {
        const field = this.$refs.html;
        const result = insertTokenAtSelection(this.value, token, field && field.selectionStart, field && field.selectionEnd);
        this.$emit('input', result.text);
        this.$nextTick(() => {
          if (field) { field.focus(); field.setSelectionRange(result.cursor, result.cursor); }
        });
        return;
      }
      if (!this.editor) return;

      const { from, to, empty } = this.editor.state.selection;
      const plan = insertionPlan({ kind, token, hasSelection: !empty });

      if (plan.action === 'wrapLink') {
        this.editor.commands.link({ href: plan.href });
      } else if (empty) {
        this.editor.view.dispatch(this.editor.state.tr.insertText(plan.text));
      } else {
        this.editor.view.dispatch(this.editor.state.tr.insertText(plan.text, from, to));
      }

      this.editor.focus();
    },

    /**
     * Public: move focus into whichever surface is currently shown.
     */
    focus() {
      if (this.htmlView) {
        if (this.$refs.html) this.$refs.html.focus();
        return;
      }
      if (this.editor) this.editor.focus();
    },

    openLinkPopover() {
      if (this.toolsDisabled) return;
      const attrs = this.editor && this.editor.getMarkAttrs
        ? this.editor.getMarkAttrs('link')
        : null;
      this.linkHref = (attrs && attrs.href) || '';
      this.linkOpen = true;
      this.$nextTick(() => {
        if (this.$refs.linkInput) this.$refs.linkInput.focus();
      });
    },

    applyLink() {
      const href = (this.linkHref || '').trim();
      if (!href) {
        this.removeLink();
        return;
      }
      this.run('link', { href, target: '_blank' });
      this.linkOpen = false;
    },

    removeLink() {
      this.run('link', { href: null });
      this.linkHref = '';
      this.linkOpen = false;
    },

    pickImage() {
      if (this.toolsDisabled) return;
      if (this.$refs.file) this.$refs.file.click();
    },

    onFileChosen(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      const done = this.uploadAndInsert(file);
      input.value = '';
      return done;
    },

    onDrop(event) {
      if (this.toolsDisabled) return;
      const file = firstImageFile(event.dataTransfer && event.dataTransfer.files);
      if (!file) return;
      event.preventDefault();
      event.stopPropagation();
      this.uploadAndInsert(file);
    },

    onPaste(event) {
      if (this.toolsDisabled) return;
      const file = firstImageFile(event.clipboardData && event.clipboardData.files);
      if (!file) return;
      event.preventDefault();
      event.stopPropagation();
      this.uploadAndInsert(file);
    },

    async uploadAndInsert(file) {
      const check = validateImageFile(file, { maxBytes: this.maxImageBytes });
      if (!check.ok) {
        this.reportUploadError(check.reason);
        return;
      }
      if (!this.uploadUrl) {
        this.reportUploadError('no-upload-url');
        return;
      }

      try {
        const url = await uploadImage(file, { uploadUrl: this.uploadUrl, axios });
        if (!url) throw new Error('missing url');
        this.run('image', { src: url });
      } catch (error) {
        this.reportUploadError('upload-failed');
      }
    },

    reportUploadError(reason) {
      this.$emit('upload-error', reason);
      // Same sentence the legacy Quill uploader alerted with, so an oversize
      // image still reports the size limit in the merchant's own language.
      const message = reason === 'too-large'
        ? this.$t('upload_file_less_than_2mb')
        : UPLOAD_MESSAGES[reason] || UPLOAD_MESSAGES['upload-failed'];
      if (this.$gsToast && typeof this.$gsToast.error === 'function') {
        this.$gsToast.error(message);
      }
    },

    trackSelectedImage() {
      if (this.toolsDisabled) {
        this.selectedImage = null;
        return;
      }
      const selection = this.editor && this.editor.state ? this.editor.state.selection : null;
      const node = selection && selection.node;
      this.selectedImage = node && node.type && node.type.name === 'image' ? node : null;
    },

    setImageAttr(attrs) {
      if (this.toolsDisabled) return;
      if (!this.editor || !this.selectedImage) return;
      const { from } = this.editor.state.selection;
      this.editor.view.dispatch(
        this.editor.state.tr.setNodeMarkup(from, null, {
          ...this.selectedImage.attrs,
          ...attrs,
        })
      );
    },

    deleteImage() {
      if (this.toolsDisabled) return;
      if (!this.editor || !this.selectedImage) return;
      this.editor.view.dispatch(this.editor.state.tr.deleteSelection());
      this.selectedImage = null;
    },

    clearFormatting() {
      if (!this.editor) return;
      MARKS_TO_CLEAR.forEach((mark) => {
        if (!this.isActiveControl(mark)) return;
        if (mark === 'link') this.run('link', { href: null });
        else this.run(mark);
      });
      this.run('paragraph');
    },
  },
};
</script>
